/**
 * MBA Lab — shared rules-based checks for a drafted message or question.
 * Pure, original functions, each implementing one named, specific check a
 * well-written accountability message or coaching question tends to satisfy.
 * No AI call, no external service - simple text heuristics, same spirit as
 * this project's other rules-based validators (planner-model.js's own
 * field-length/shape checks).
 *
 * Consumed by two different UI contexts that both need "does this drafted
 * text meet the bar": the scenario simulator's write-type tree nodes, and
 * the accountability planner's draft-message-review mode. Living here once,
 * rather than duplicated in both models, is the point.
 */

const EMOTION_WORDS = Object.freeze([
  'frustrated', 'disappointed', 'worried', 'concerned', 'confused', 'embarrassed',
  'anxious', 'discouraged', 'uneasy', 'uncomfortable', 'surprised', 'hurt',
]);

const ADVICE_MARKERS = Object.freeze([
  'you should', 'have you tried', 'what i would do', "why don't you", 'why dont you',
]);

const REQUEST_MARKERS = Object.freeze([
  'going forward', 'my request', 'i need', 'would you', 'could you', 'can you',
]);

const ABSOLUTE_WORDS = Object.freeze(['always', 'never', 'every time']);

const NEGATED_DEADLINE = /\b(no|not a|isn't a|aren't any|without a|without any)\s+(specific\s+)?deadline\b/;

const WEEKDAYS = Object.freeze(['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']);
const MONTHS = Object.freeze([
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
]);

function toText(value) {
  return typeof value === 'string' ? value : '';
}

/** Lowercases and normalizes curly quotes/apostrophes to straight ones, so a
 * marker list only needs one spelling - real user input and this project's
 * own house-style prose (which uses curly apostrophes throughout) must both
 * match the same "don't"-shaped marker. */
export function lower(value) {
  return toText(value)
    .toLowerCase()
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"');
}

/** Word-boundary match for a single phrase - "never" must not match inside
 * "whenever", "concerned" must not match inside "unconcerned". `\b` is based
 * on \w (letters/digits/underscore), which is exactly right for these
 * all-lowercase-letter phrases; multi-word phrases still work because `\b`
 * only needs to hold at the two ends of the whole phrase, not internally. */
export function containsPhrase(haystack, phrase) {
  const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`\\b${escaped}\\b`).test(haystack);
}

export function containsAny(haystack, needles) {
  return needles.some((n) => containsPhrase(haystack, n));
}

/** Splits on sentence-ending punctuation, dropping empty fragments. */
function sentences(text) {
  return toText(text)
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** The opening clause: everything up to the first comma, or the first 8
 * words if there's no comma - matches how a "you"-led opening actually
 * reads, without requiring the whole sentence to avoid the word "you". */
function openingClause(text) {
  const trimmed = toText(text).trim();
  const commaIndex = trimmed.indexOf(',');
  if (commaIndex > -1) return trimmed.slice(0, commaIndex);
  return trimmed.split(/\s+/).slice(0, 8).join(' ');
}

const SPELLED_OUT_COUNTS = Object.freeze([
  'once', 'twice', 'three times', 'four times', 'five times', 'several times', 'a few times', 'multiple times',
]);

const RELATIVE_TIME_ANCHORS = Object.freeze([
  'yesterday', 'today', 'this morning', 'this afternoon', 'last night', 'earlier today',
]);

export function hasObservable(text) {
  const t = lower(text);
  if (containsAny(t, WEEKDAYS) || containsAny(t, MONTHS)) return true;
  if (/\b\d+\s*(times|x)\b/.test(t)) return true; // "3 times", "2x"
  if (containsAny(t, SPELLED_OUT_COUNTS)) return true; // "three times", "a few times"
  if (/\b(first|second|third|fourth|fifth|sixth)\s+time\b/.test(t)) return true; // "the fourth time"
  if (containsAny(t, RELATIVE_TIME_ANCHORS)) return true; // "yesterday's meeting"
  if (containsAny(t, ['when you', 'last week', 'last month'])) return true;
  if (/\b\d{1,2}\/\d{1,2}\b/.test(t)) return true; // a date like 7/28
  return false;
}

export function avoidsYouStem(text) {
  return !/^\s*you\b/i.test(openingClause(text));
}

export function oneEmotion(text) {
  const t = lower(text);
  const matches = EMOTION_WORDS.filter((w) => containsPhrase(t, w));
  return matches.length === 1;
}

export function noJudgmentAsEmotion(text) {
  const t = lower(text);
  // Both the full and contracted forms of "you are" - a real gap found by
  // audit: "I feel you're being unfair" is the exact judgment-as-feeling
  // pattern this check exists to catch, and the more common phrasing of it.
  return !containsAny(t, ['i feel that', 'i feel like you', 'i feel you are', "i feel you're"]);
}

export function hasRequest(text) {
  return containsAny(lower(text), REQUEST_MARKERS);
}

export function endsWithQuestion(text) {
  const s = sentences(text);
  if (!s.length) return false;
  return s[s.length - 1].endsWith('?');
}

export function noAbsolutes(text) {
  return !containsAny(lower(text), ABSOLUTE_WORDS);
}

export function hasSpecificDate(text) {
  const t = lower(text);
  if (containsAny(t, WEEKDAYS) || containsAny(t, MONTHS)) return true;
  // "deadline" alone isn't enough - "there is no deadline set" mentions the
  // word while asserting the opposite of what this check means to detect.
  if (/\bdeadline\b/.test(t) && !NEGATED_DEADLINE.test(t)) return true;
  if (/\b\d{1,2}\/\d{1,2}\b/.test(t)) return true;
  return false;
}

export function isOpenQuestion(text) {
  const t = lower(text).trim();
  if (!t) return false;
  if (/^(what|how)\b/.test(t)) return true;
  if (t.startsWith('tell me')) return true;
  if (/^(why|did|do|does|are|is)\b/.test(t)) return false;
  return false;
}

export function underSevenWords(text) {
  const words = toText(text).trim().split(/\s+/).filter(Boolean);
  return words.length > 0 && words.length < 7;
}

export function noAdvice(text) {
  return !containsAny(lower(text), ADVICE_MARKERS);
}

export const CHECKS = Object.freeze({
  hasObservable,
  avoidsYouStem,
  oneEmotion,
  noJudgmentAsEmotion,
  hasRequest,
  endsWithQuestion,
  noAbsolutes,
  hasSpecificDate,
  isOpenQuestion,
  underSevenWords,
  noAdvice,
});

export const CHECK_LABELS = Object.freeze({
  hasObservable: 'Names something concrete and datable',
  avoidsYouStem: 'Doesn’t open with "you"',
  oneEmotion: 'Names exactly one feeling',
  noJudgmentAsEmotion: 'States a feeling, not a judgment dressed as one',
  hasRequest: 'Makes a forward-looking request',
  endsWithQuestion: 'Ends by asking, not telling',
  noAbsolutes: 'Avoids "always" / "never" / "every time"',
  hasSpecificDate: 'Names a specific date or deadline',
  isOpenQuestion: 'Is an open question (what/how), not why/did/do/are/is',
  underSevenWords: 'Is under seven words',
  noAdvice: 'Doesn’t slip into giving advice',
});

/** Runs a named subset of checks against text and returns {id, label, passed}
 * for each - the shape both consuming UIs render directly as a checklist.
 *
 * Returns an empty array for blank/whitespace-only text, rather than
 * running the checks anyway. Most of these checks are "does NOT contain X"
 * shaped (avoidsYouStem, noAdvice, noAbsolutes, noJudgmentAsEmotion), which
 * are vacuously true on empty text - an untouched textarea would otherwise
 * show several checks as already "passed" before anyone typed anything.
 * Both UIs render this list directly with a for-loop, so an empty array
 * already means "show nothing" with no separate change needed there. */
export function runChecks(text, checkIds) {
  if (!toText(text).trim()) return [];
  return checkIds
    .filter((id) => typeof CHECKS[id] === 'function')
    .map((id) => ({ id, label: CHECK_LABELS[id], passed: CHECKS[id](text) }));
}
